/**
 * Persistent shared counter for the PWA app-icon badge.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/BadgeCountStore.swift` — single source
 * of truth for the badge total, shared between the main thread and the Service
 * Worker via IndexedDB (both run in the same origin and therefore access the
 * same IDB databases).
 *
 * THREAT MODEL
 *   Two execution contexts touch one IDB record: the Service Worker increments
 *   when a push notification is rendered, and the main thread decrements when
 *   the user emits a read receipt. Two contexts touching one record is a race
 *   risk, so:
 *
 *   - Every read-modify-write sequence is serialised through an exclusive IDB
 *     transaction (mode: "readwrite"). IDB's transaction isolation means only
 *     one readwrite transaction on the same object store can be open at a time
 *     within the same origin — making every increment/decrement atomic in the
 *     same way iOS uses a serial DispatchQueue barrier.
 *   - `rebase(to)` is the recovery primitive. After a backup restore, or if
 *     the increment/decrement deltas have diverged from the actual unread-feed
 *     total, the main thread recomputes from the unread store and writes the
 *     canonical value here. This is the same strategy Signal-iOS uses.
 *   - Unlike iOS `.completeFileProtection`, IndexedDB has no device-lock
 *     encryption equivalent on the web. This is documented in
 *     `Signal_standard_web.md` table row "`.completeFileProtection`". The
 *     trade-off mirrors how all other IDB-backed E2EE state is handled.
 *
 * Service-Worker importability guarantee:
 *   This file uses NO `window`, `document`, or DOM-only APIs.
 *   It uses only IndexedDB (available in both SW and main thread).
 */

import {
  clampBadgeCount,
  DISTANT_PAST,
  MAX_BADGE_COUNT,
} from "./BadgeCountValue";
import type { BadgeCountError, BadgeCountSnapshot } from "./BadgeCountValue";

// ---------------------------------------------------------------------------
// IDB wiring
// ---------------------------------------------------------------------------

const DB_VERSION = 1;
const STORE_NAME = "badge-count";
const RECORD_KEY = "singleton";

/** Shape persisted in IndexedDB. JSON-serialisable (numbers only). */
interface PersistedRecord {
  total: number;
  lastUpdatedAt: number; // unix ms, or -Infinity encoded as null
}

function openDb(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function readRecord(db: IDBDatabase): Promise<PersistedRecord | null> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(RECORD_KEY);
    req.onsuccess = () => {
      const val = req.result as PersistedRecord | undefined;
      resolve(val ?? null);
    };
    req.onerror = () => reject(req.error);
  });
}

function writeRecord(db: IDBDatabase, rec: PersistedRecord): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(rec, RECORD_KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Run a read-modify-write inside a single IDB "readwrite" transaction so the
 * whole operation is atomic (IDB guarantees no other readwrite TX on the same
 * store can interleave).
 */
function atomicRMW(
  db: IDBDatabase,
  mutate: (prev: PersistedRecord) => PersistedRecord,
): Promise<PersistedRecord> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const getReq = store.get(RECORD_KEY);
    getReq.onsuccess = () => {
      const prev: PersistedRecord = getReq.result ?? {
        total: 0,
        lastUpdatedAt: null as unknown as number, // stored as null for DISTANT_PAST
      };
      const next = mutate(prev);
      store.put(next, RECORD_KEY);
      tx.oncomplete = () => resolve(next);
      tx.onerror = () => reject(tx.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

// ---------------------------------------------------------------------------
// Snapshot helpers
// ---------------------------------------------------------------------------

function toSnapshot(rec: PersistedRecord | null): BadgeCountSnapshot {
  if (!rec) {
    return { total: 0, lastUpdatedAt: DISTANT_PAST };
  }
  // `lastUpdatedAt` is stored as `null` in JSON when the value is -Infinity
  // (JSON.stringify(-Infinity) → "null"). Restore faithfully.
  const ts = rec.lastUpdatedAt == null ? DISTANT_PAST : rec.lastUpdatedAt;
  return { total: rec.total, lastUpdatedAt: ts };
}

function fromSnapshot(snap: BadgeCountSnapshot): PersistedRecord {
  return {
    total: snap.total,
    lastUpdatedAt: snap.lastUpdatedAt === DISTANT_PAST
      ? (null as unknown as number)
      : snap.lastUpdatedAt,
  };
}

// ---------------------------------------------------------------------------
// BadgeCountStore
// ---------------------------------------------------------------------------

/**
 * IndexedDB-backed badge counter. Web equivalent of iOS `BadgeCountStore`.
 *
 * Usage — instantiate per-context (main thread / Service Worker) with the
 * same `dbName` so both contexts share state:
 *
 * ```ts
 * const store = new BadgeCountStore();
 * const snap = await store.current();
 * await store.increment(1);
 * ```
 *
 * `navigator.setAppBadge()` is intentionally NOT called here — the consumer
 * is responsible for reading `.current().total` and driving the OS badge.
 */
export class BadgeCountStore {
  static readonly MAX = MAX_BADGE_COUNT;

  /** Shared singleton for the default DB name. Lazy-initialised. */
  private static _shared: BadgeCountStore | null = null;

  static get shared(): BadgeCountStore {
    if (!BadgeCountStore._shared) {
      BadgeCountStore._shared = new BadgeCountStore();
    }
    return BadgeCountStore._shared;
  }

  private readonly dbName: string;
  private _db: IDBDatabase | null = null;

  constructor(dbName = "kaether:badge-count:v1") {
    this.dbName = dbName;
  }

  private async db(): Promise<IDBDatabase> {
    if (!this._db) {
      this._db = await openDb(this.dbName);
    }
    return this._db;
  }

  // --------------------------------------------------------------------------
  // Read
  // --------------------------------------------------------------------------

  /**
   * Returns the current snapshot. If the record does not exist (first launch,
   * post-`reset`), returns total=0 with `lastUpdatedAt = DISTANT_PAST` so
   * callers can distinguish "never written" from "we wrote a zero just now".
   */
  async current(): Promise<BadgeCountSnapshot> {
    const db = await this.db();
    const rec = await readRecord(db);
    return toSnapshot(rec);
  }

  // --------------------------------------------------------------------------
  // Mutations
  // --------------------------------------------------------------------------

  /**
   * Increments the counter by `delta` (must be > 0; 0 is a no-op; negative
   * is clamped to 0). The result is clamped at MAX_BADGE_COUNT.
   * Mirrors iOS `BadgeCountStore.increment(by:at:)`.
   */
  async increment(
    delta: number,
    at: number = Date.now(),
  ): Promise<BadgeCountSnapshot> {
    if (delta <= 0) {
      return this.current();
    }
    const db = await this.db();
    const next = await atomicRMW(db, (prev) => {
      const prevTotal = prev.total ?? 0;
      const raw = prevTotal + delta;
      // Saturating add — mirror iOS's `&+` clamp logic.
      const clamped = clampBadgeCount(raw);
      return fromSnapshot({ total: clamped, lastUpdatedAt: at });
    });
    return toSnapshot(next);
  }

  /**
   * Decrements the counter by `delta`, clamped at zero. The result is never
   * negative. Mirrors iOS `BadgeCountStore.decrement(by:at:)`.
   */
  async decrement(
    delta: number,
    at: number = Date.now(),
  ): Promise<BadgeCountSnapshot> {
    if (delta <= 0) {
      return this.current();
    }
    const db = await this.db();
    const next = await atomicRMW(db, (prev) => {
      const prevTotal = prev.total ?? 0;
      const clamped = clampBadgeCount(prevTotal - delta);
      return fromSnapshot({ total: clamped, lastUpdatedAt: at });
    });
    return toSnapshot(next);
  }

  /**
   * Reconciles the counter to a known-good total. Used after backup restore,
   * account-credential reinstatement, or when increment/decrement deltas have
   * diverged from the actual unread feed sum.
   *
   * Throws `{ kind: 'negativeCount', claimed }` if `total < 0`.
   * The result is clamped at MAX_BADGE_COUNT.
   *
   * Mirrors iOS `BadgeCountStore.rebase(to:at:)`.
   */
  async rebase(
    total: number,
    at: number = Date.now(),
  ): Promise<BadgeCountSnapshot> {
    if (total < 0) {
      const err: BadgeCountError = { kind: "negativeCount", claimed: total };
      throw err;
    }
    const clamped = clampBadgeCount(total);
    const db = await this.db();
    const snap: BadgeCountSnapshot = { total: clamped, lastUpdatedAt: at };
    await writeRecord(db, fromSnapshot(snap));
    return snap;
  }

  /**
   * Removes the IDB record so the next `current()` returns the fresh-store
   * sentinel. Intended for tests and "mark all as read" flows.
   * Mirrors iOS `BadgeCountStore.reset()`.
   */
  async reset(): Promise<void> {
    const db = await this.db();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).delete(RECORD_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
}
